/**
 * 첨부 줄 — **보내기 전에 붙여 둔 파일**. 채팅의 첨부와 같다: 다음 메시지와 함께 가고,
 * 보내면 여기서 사라진다(메시지 말풍선에 남는다).
 *
 * 전에는 한 번 올리면 계속 남아 매 턴에 다시 실렸다 — 사용자에게는 "첨부가 안 지워진다" 로
 * 보였고, 비용은 턴마다 늘었다. 앞 턴의 첨부는 SDK 세션이 기억하므로 다시 보낼 필요가 없다.
 *
 * 파일을 올리면 서버가 텍스트를 뽑는다. 사례 등록에 필요한 내용(수정 전/후 쿼리·실행계획)이
 * 있는지도 함께 보여 준다 — 대화까지 가서야 "등록 불가" 를 듣는 것보다 낫다.
 *
 * 업로드 자체는 **패널이 가지고 있다** — 클립보드 붙여넣기도 같은 경로로 들어와야
 * 이름 규칙·검증이 갈리지 않는다.
 */
import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { FiPaperclip, FiX, FiAlertCircle, FiImage, FiFileText } from 'react-icons/fi';
import { deleteTuningSource } from '../../services/assistantClient';
// 형식 목록은 끌어다 놓기와 같은 한 곳 — 고르면 되는데 끌어 놓으면 안 되는 일이 없게
import { ATTACH_ACCEPT } from '../../utils/dropFiles';
import { topSources, childCount } from '../../utils/sourceTree';
import styles from './AssistantPanel.module.css';

const ACCEPT = ATTACH_ACCEPT;

function CaseSourceBar({ sources, onChange, onAdd, busy, disabled }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);

    const pick = useCallback((e) => {
        const files = [...(e.target.files || [])];
        e.target.value = '';                       // 같은 파일을 다시 고를 수 있게
        if (files.length) onAdd(files);
    }, [onAdd]);

    const remove = useCallback(async (id) => {
        try { await deleteTuningSource(id); } catch (_e) { /* 서버에 이미 없을 수 있다 */ }
        // 문서를 빼면 그 문서에서 꺼낸 그림도 함께 뺀다(서버도 함께 지운다)
        onChange(sources.filter(s => s.id !== id && s.parentId !== id));
    }, [sources, onChange]);

    return (
        <div className={styles.sourceBar}>
            <input
                ref={inputRef} type="file" multiple accept={ACCEPT}
                onChange={pick} style={{ display: 'none' }}
            />
            <button
                type="button" className={styles.sourceAddBtn}
                onClick={() => inputRef.current && inputRef.current.click()}
                disabled={disabled || busy}
                title={`${t('assistant:case.addHint')}\n${t('assistant:case.pasteHint')}\n${t('assistant:case.dropHint')}`}
            >
                <FiPaperclip size={11} /> {busy ? t('assistant:case.uploading') : t('assistant:case.add')}
            </button>

            {/* 문서(PPTX·DOCX)에서 꺼낸 그림은 **칩으로 따로 세우지 않는다** — 파일 하나를 붙였는데 여러 개로 갈라져 보였다(신고 2026-10-13).
                그림은 그대로 문서와 함께 간다(AI 가 캡처로 붙은 쿼리·실행계획을 읽는 데 쓴다) — 문서 칩에 장 수만 적는다 */}
            {topSources(sources).map((s) => {
                const inner = childCount(sources, s.id);
                const isImage = s.kind === 'image';
                const bad = s.error || (s.required && !s.required.ok && !s.required.deferred);
                return (
                    <span
                        key={s.id || s.filename}
                        className={`${styles.sourceChip} ${bad ? styles.sourceChipWarn : ''}`}
                        title={s.error || (s.required && s.required.reason) || s.note || s.filename}
                    >
                        {isImage ? <FiImage size={10} /> : <FiFileText size={10} />}
                        <span className={styles.sourceName}>{s.filename}</span>
                        {inner ? <span className={styles.sourceInner} title={t('assistant:case.innerImagesHint', { count: inner })}>{t('assistant:case.innerImages', { count: inner })}</span> : null}
                        {bad ? <FiAlertCircle size={10} /> : null}
                        {s.id ? (
                            <button
                                type="button" className={styles.sourceDel}
                                onClick={() => remove(s.id)} title={t('common:ui.delete')}
                            >
                                <FiX size={10} />
                            </button>
                        ) : null}
                    </span>
                );
            })}

            {/* 사례 등록에 필요한 내용이 없을 때만. 일반 첨부에도 뜨므로 "사례 등록 시" 라고 범위를 밝힌다 */}
            {sources.some(s => s.required && !s.required.ok && !s.required.deferred) && (
                <span className={styles.sourceWarn}>{t('assistant:case.missingRequired')}</span>
            )}
        </div>
    );
}

CaseSourceBar.propTypes = {
    sources: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,

    onAdd: PropTypes.func.isRequired,
    busy: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default CaseSourceBar;
